<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { defineSchema, $ } from '@bagelink/vue'
const defaults = {
	name: 'popup',
	isActive: 'true',
	position: 'center',
	startDate: '',
	endDate: '',
	content: '<p>Popup content here</p>',
	buttonText: 'Learn More',
	buttonLink: '',
	recurringDays: '0',
	pageVisitsBeforeShow: '1',
}
const schema = defineSchema({
	name: $.text('Popup Name'),
	isActive: $.select('Active', [
		{ label: 'Yes', value: 'true' },
		{ label: 'No', value: 'false' },
	]),
	position: $.select('Position', [
		{ label: 'Center', value: 'center' },
		{ label: 'Bottom Right', value: 'bottom-right' },
		{ label: 'Bottom Left', value: 'bottom-left' },
	]),
	startDate: $.date('Start Date'),
	endDate: $.date('End Date'),
	content: $.richtext('Content', { basic: true }),
	buttonText: $.text('Button Text'),
	buttonLink: $.text('Button Link'),
	recurringDays: $.text('Recurring Days'),
	pageVisitsBeforeShow: $.text('Page Visits Before Show'),
})
export const BlockPopup = defineBlock({ pickerHidden: true, thumbnail: '/block-thumbs/BlockPopup.svg', category: 'Utility', order: 250, name: 'BlockPopup', label: 'Popup', icon: 'web_asset', schema, defaults })
</script>
<script setup lang="ts">

import { Btn, Dialog } from '@bagelink/vue'
import { computed, onMounted, ref } from 'vue'
import RichHtml from '@/components/RichHtml.vue'




const props = defineProps<{
	name?: string
	isActive?: string
	position?: 'center' | 'bottom-right' | 'bottom-left'
	startDate?: string
	endDate?: string
	content?: string
	buttonText?: string
	buttonLink?: string
	recurringDays?: string
	pageVisitsBeforeShow?: string
}>()

const isVisible = ref(false)

const lastShownKey = computed(() => `popup-${props.name}-lastShown`)
const pageVisitsKey = computed(() => `popup-${props.name}-pageVisits`)

function checkShouldShow(): boolean {
	if (props.isActive !== 'true') return false
	if (typeof localStorage === 'undefined') return false

	if (props.startDate) {
		const startDate = new Date(props.startDate)
		if (new Date() < startDate) return false
	}

	if (props.endDate) {
		const endDate = new Date(props.endDate)
		if (new Date() > endDate) return false
	}

	const recurring = Number.parseInt(props.recurringDays || '0')
	const lastShown = localStorage.getItem(lastShownKey.value)
	if (lastShown && recurring > 0) {
		const daysSinceLastShown = (Date.now() - Number.parseInt(lastShown)) / (1000 * 60 * 60 * 24)
		if (daysSinceLastShown < recurring) return false
	}

	const minVisits = Number.parseInt(props.pageVisitsBeforeShow || '1')
	const currentVisits = Number.parseInt(localStorage.getItem(pageVisitsKey.value) || '0') + 1
	localStorage.setItem(pageVisitsKey.value, currentVisits.toString())

	if (currentVisits < minVisits) return false

	return true
}

function closePopup() {
	isVisible.value = false
	if (typeof localStorage !== 'undefined') {
		localStorage.setItem(lastShownKey.value, Date.now().toString())
		localStorage.setItem(pageVisitsKey.value, '0')
	}
}

function handleModalClose(visible: boolean) {
	if (!visible) {
		closePopup()
	}
}

function handleButtonClick() {
	if (props.buttonLink && typeof window !== 'undefined') {
		window.open(props.buttonLink, '_blank')
	}
	closePopup()
}

onMounted(() => {
	if (checkShouldShow()) {
		isVisible.value = true
	}
})
</script>

<template>
	<!-- Use Modal for center position -->
	<Dialog
		v-if="position === 'center'"
		:open="isVisible"
		width="l"
		@update:open="(v: boolean) => { isVisible = v; handleModalClose(v) }"
	>
		<RichHtml class="popup-text" :html="content" />

		<div v-if="buttonText" class="txt-center mt-2">
			<Btn class="px-3 redhoverBg" outline color="primary" @click="handleButtonClick">
				{{ buttonText }}
			</Btn>
		</div>
	</Dialog>

	<!-- Keep original overlay for bottom-right and bottom-left positions -->
	<div v-else-if="isVisible" class="popup-container w300px shadow radius-2 fixed z-99 bg-gray bottom-05 overflow" :class="{ 'end-05': position === 'bottom-right', 'start-05': position === 'bottom-left' }">
		<Btn flat icon="close" @click="closePopup" />

		<div class="popup-content p-2">
			<RichHtml class="popup-text" :html="content" />

			<div v-if="buttonText" class="txt-center mt-2">
				<Btn class="px-3 redhoverBg" outline color="primary" @click="handleButtonClick">
					{{ buttonText }}
				</Btn>
			</div>
		</div>
	</div>
</template>

<style>

</style>
